import { useState } from "react";
import { Loader2 } from "lucide-react";
import { Card, CardContent } from "../../../components/ui/card";
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, Legend, CartesianGrid,
} from "recharts";

const COLORS = ["#ff3737", "#3b82f6", "#22c55e", "#f59e0b", "#8b5cf6", "#ec4899", "#06b6d4"];

const formatDateLabel = (v) => {
  if (!v) return v;
  const parts = v.split("-");
  if (parts.length === 3) {
    const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    const m = parseInt(parts[1], 10);
    return `${months[m - 1]} ${parseInt(parts[2], 10)}`;
  }
  return v;
};

const formatDateFull = (v) => {
  if (!v) return v;
  const parts = v.split("-");
  if (parts.length === 3) {
    const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    const m = parseInt(parts[1], 10);
    return `${parseInt(parts[2], 10)} ${months[m - 1]} ${parts[0]}`;
  }
  return v;
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#1a1c1e] border border-white/10 rounded-lg p-3 text-xs font-mono shadow-xl">
      <p className="text-[#ebebeb]/70 mb-1">{formatDateFull(label)}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }}>
          {p.name}: {typeof p.value === "number" ? p.value.toLocaleString(undefined, { maximumFractionDigits: 2 }) : p.value}
        </p>
      ))}
    </div>
  );
};

const MarketplaceCharts = ({ breakdownBySSP, trendData, loading, revenueVisible = true, lightMode = false }) => {
  const [chartMetric, setChartMetric] = useState(revenueVisible ? "revenue" : "spend");

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-6 h-6 text-[#ff3737] animate-spin" />
      </div>
    );
  }

  const hasData = breakdownBySSP?.length > 0 || trendData?.length > 0;
  if (!hasData) return null;

  const metrics = [
    ...(revenueVisible ? [{ key: "revenue", label: "Revenue" }] : []),
    { key: "spend", label: "Spend" },
    { key: "impressions", label: "Impressions" },
    { key: "clicks", label: "Clicks" },
    ...(revenueVisible ? [{ key: "margin_pct", label: "Margin %" }] : []),
    { key: "cpm", label: "CPM" },
    { key: "cpc", label: "CPC" },
    { key: "ctr", label: "CTR" },
  ];

  // Prepare pie data for SSP breakdown
  const pieData = (breakdownBySSP || []).map((b, i) => ({
    name: b.label,
    value: b[chartMetric] || 0,
    fill: COLORS[i % COLORS.length],
  })).filter(d => d.value > 0);

  // Prepare trend data with derived per-day metrics
  const trendFormatted = (trendData || []).map((d) => {
    const imps = d.impressions || 0;
    const clicks = d.clicks || 0;
    const spend = d.spend || 0;
    return {
      date: d.label,
      revenue: d.revenue || 0,
      spend,
      impressions: imps,
      clicks,
      margin_pct: d.margin_pct || 0,
      cpm: imps > 0 ? +(spend / imps * 1000).toFixed(2) : 0,
      cpc: clicks > 0 ? +(spend / clicks).toFixed(2) : 0,
      ctr: imps > 0 ? +(clicks / imps * 100).toFixed(4) : 0,
    };
  });

  const cardCls = lightMode ? "bg-white border-gray-200 shadow-sm" : "bg-[#1e2124] border-[#2a2d31]";
  const labelCls = lightMode ? "text-gray-400" : "text-[#ebebeb]/60";
  const gridStroke = lightMode ? "#e5e7eb" : "#2a2d31";
  const tickFill = lightMode ? "#6b728080" : "#ebebeb60";
  const legendCls = lightMode ? "text-gray-600" : "text-[#ebebeb]/70";

  return (
    <div className="space-y-6">
      {/* Metric Toggle */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`text-xs font-mono mr-2 ${lightMode ? "text-gray-400" : "text-[#ebebeb]/40"}`}>Metric:</span>
        {metrics.map((m) => (
          <button
            key={m.key}
            onClick={() => setChartMetric(m.key)}
            className={`px-3 py-1.5 rounded-lg text-xs font-mono transition-colors ${
              chartMetric === m.key
                ? "bg-[#ff3737]/15 text-[#ff3737] border border-[#ff3737]/30"
                : lightMode
                  ? "text-gray-500 border border-gray-200 hover:bg-gray-50"
                  : "text-[#ebebeb]/50 border border-white/10 hover:bg-white/5"
            }`}
            data-testid={`chart-metric-${m.key}`}
          >
            {m.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Line Chart - Trends Over Time */}
        {trendFormatted.length > 1 && (
          <Card className={cardCls}>
            <CardContent className="p-4">
              <p className={`text-xs font-mono mb-4 uppercase tracking-wider ${labelCls}`}>Daily Trend</p>
              <div className="h-[280px]" data-testid="trend-line-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trendFormatted}>
                    <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
                    <XAxis dataKey="date" tick={{ fill: tickFill, fontSize: 10, fontFamily: "monospace" }} tickFormatter={formatDateLabel} />
                    <YAxis tick={{ fill: tickFill, fontSize: 10, fontFamily: "monospace" }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Line type="monotone" dataKey={chartMetric} stroke="#ff3737" strokeWidth={2} dot={false} activeDot={{ r: 4, fill: "#ff3737" }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Pie Chart - SSP Breakdown */}
        {pieData.length > 0 && (
          <Card className={cardCls}>
            <CardContent className="p-4">
              <p className={`text-xs font-mono mb-4 uppercase tracking-wider ${labelCls}`}>By SSP Source</p>
              <div className="h-[280px]" data-testid="ssp-pie-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={pieData} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={2} dataKey="value">
                      {pieData.map((_, i) => (<Cell key={i} fill={COLORS[i % COLORS.length]} />))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                    <Legend formatter={(value) => <span className={`text-xs font-mono ${legendCls}`}>{value}</span>} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Bar Chart - SSP Comparison */}
        {breakdownBySSP?.length > 0 && (
          <Card className={`${cardCls} lg:col-span-2`}>
            <CardContent className="p-4">
              <p className={`text-xs font-mono mb-4 uppercase tracking-wider ${labelCls}`}>SSP Comparison</p>
              <div className="h-[280px]" data-testid="ssp-bar-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={breakdownBySSP}>
                    <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
                    <XAxis dataKey="label" tick={{ fill: tickFill, fontSize: 10, fontFamily: "monospace" }} />
                    <YAxis tick={{ fill: tickFill, fontSize: 10, fontFamily: "monospace" }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend formatter={(value) => <span className={`text-xs font-mono ${legendCls}`}>{value}</span>} />
                    {revenueVisible && <Bar dataKey="revenue" fill="#22c55e" name="Revenue" radius={[4, 4, 0, 0]} />}
                    <Bar dataKey="spend" fill="#3b82f6" name="Spend" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default MarketplaceCharts;
